import { useState } from 'react';
import type { Order } from '../../types/ordering.types';
import { formatOrderAmount, isOverdue } from '../../types/ordering.types';
import { OrderStatusPill } from './OrderStatusPill';
import type { Person } from '../../types/timesheet.types';
import { ORDER_SELF_APPROVAL_LIMIT } from '../../utils/constants';

interface Column {
  key: string;
  label: string;
  render: (o: Order) => React.ReactNode;
  align?: 'left' | 'right' | 'center';
  style?: React.CSSProperties;
  sortable?: boolean;
  sortValue?: (o: Order) => string | number;
}

interface Props {
  orders: Order[];
  columns: Column[];
  onRowClick: (order: Order) => void;
  emptyMessage?: string;
}

type SortDir = 'asc' | 'desc';

export function OrderListTable({ orders, columns, onRowClick, emptyMessage = 'No orders found.' }: Props) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  if (orders.length === 0) {
    return <div className="ord-empty">{emptyMessage}</div>;
  }

  function handleHeaderClick(col: Column) {
    if (!col.sortable) return;
    if (sortKey === col.key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(col.key);
      setSortDir('asc');
    }
  }

  const sorted = (() => {
    const arr = [...orders];
    if (sortKey) {
      const col = columns.find(c => c.key === sortKey);
      if (col?.sortValue) {
        arr.sort((a, b) => {
          const va = col.sortValue!(a);
          const vb = col.sortValue!(b);
          const cmp = va < vb ? -1 : va > vb ? 1 : 0;
          return sortDir === 'asc' ? cmp : -cmp;
        });
        return arr;
      }
    }
    // Default: newest first by id
    return arr.sort((a, b) => Number(b.id) - Number(a.id));
  })();

  return (
    <div className="ord-table-scroll-wrap">
      <div>
        {/* Header — matches mockup admin-hdr ord-list-grid */}
        <div className="admin-hdr ord-list-grid">
          {columns.map(c => (
            <div
              key={c.key}
              style={{
                textAlign: c.align ?? 'left',
                ...(c.style ?? {}),
                ...(c.sortable ? { cursor: 'pointer', userSelect: 'none' } : {}),
              }}
              onClick={() => handleHeaderClick(c)}
            >
              {c.label}
              {c.sortable && (
                <span style={{ marginLeft: 4, opacity: sortKey === c.key ? 1 : 0.3, fontSize: 10 }}>
                  {sortKey === c.key ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}
                </span>
              )}
            </div>
          ))}
        </div>
        {/* Rows — matches mockup admin-row ord-list-grid */}
        {sorted.map(order => (
          <div
            key={order.id}
            className={`admin-row ord-list-grid${isOverdue(order) ? ' ord-row-overdue' : ''}`}
            onClick={() => onRowClick(order)}
          >
            {columns.map(c => (
              <div key={c.key} style={{ textAlign: c.align ?? 'left', ...(c.style ?? {}) }}>
                {c.render(order)}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// Pre-built column factories used across multiple views
export const COL_REF = (): Column => ({
  key: 'ref', label: 'REF', sortable: true,
  sortValue: o => o.orderRef || `ORD-${o.id}`,
  render: o => <span className="ord-ref">{o.orderRef || `ORD-${o.id}`}</span>,
});

export const COL_DATE = (field: 'orderDate' | 'created', label = 'DATE'): Column => ({
  key: field, label, sortable: true,
  sortValue: o => o[field] ?? '',
  render: o => <span className="ord-date">{fmtDate(o[field])}</span>,
});

export const COL_DESC = (): Column => ({
  key: 'title', label: 'DESCRIPTION', sortable: true,
  sortValue: o => o.title?.toLowerCase() ?? '',
  render: o => <span className="ord-desc" title={o.title}>{o.title}</span>,
});

export const COL_CATEGORY = (): Column => ({
  key: 'cat', label: 'CATEGORY', sortable: true,
  sortValue: o => o.costAllocation?.toLowerCase() ?? '',
  render: o => <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{o.costAllocation}</span>,
});

export const COL_SUPPLIER = (): Column => ({
  key: 'supplier', label: 'SUPPLIER', sortable: true,
  sortValue: o => o.supplier?.toLowerCase() ?? '',
  render: o => <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{o.supplier || '—'}</span>,
});

export const COL_EXP_DELIVERY = (): Column => ({
  key: 'expDel', label: 'EXP. DELIVERY', sortable: true,
  sortValue: o => o.expectedDeliveryDate ?? '',
  render: o => (
    <span style={{ fontSize: 12.5, fontFamily: 'DM Mono, monospace', color: isOverdue(o) ? 'var(--red)' : 'var(--text-muted)', fontWeight: isOverdue(o) ? 600 : 400 }}>
      {o.expectedDeliveryDate ? `${fmtDate(o.expectedDeliveryDate)}${isOverdue(o) ? ' ⚠' : ''}` : '—'}
    </span>
  ),
});

export const COL_TOTAL = (): Column => ({
  key: 'total', label: 'TOTAL', align: 'right',
  style: { paddingRight: 14 }, sortable: true,
  sortValue: o => o.sterlingEquivalent ?? o.totalAmount ?? 0,
  render: o => <span className="ord-total">{formatOrderAmount(o.totalAmount, o.currency)}</span>,
});

export const COL_STATUS = (): Column => ({
  key: 'status', label: 'STATUS', align: 'center', sortable: true,
  sortValue: o => o.status ?? '',
  render: o => <OrderStatusPill status={o.status} />,
});

export const COL_REQUESTER = (): Column => ({
  key: 'req', label: 'REQUESTER', sortable: true,
  sortValue: o => o.requesterName?.toLowerCase() ?? '',
  render: o => <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{o.requesterName}</span>,
});

export const COL_LM = (people?: Map<string, Person>): Column => ({
  key: 'lm', label: 'APPROVER',
  render: o => {
    const requester = people?.get(o.requesterEmail.toLowerCase());
    if (requester && !o.hasChemicalLines) {
      const orderValue = o.sterlingEquivalent ?? o.totalAmount;
      const limit = requester.purchaseApprovalLimit ?? ORDER_SELF_APPROVAL_LIMIT;
      const effectiveSelfLimit = Math.max(ORDER_SELF_APPROVAL_LIMIT, limit);
      if (orderValue <= effectiveSelfLimit) {
        return <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{requester.fullName}</span>;
      }
    }
    const name = requester?.lineManager || o.lineManagerName || '—';
    return <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{name}</span>;
  },
});

// FYI Queue: "ADDED BY" = who added this order to your FYI queue (typically requester)
export const COL_ADDED_BY = (): Column => ({
  key: 'addedBy', label: 'ADDED BY', sortable: true,
  sortValue: o => o.requesterName?.toLowerCase() ?? '',
  render: o => <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{o.requesterName || '—'}</span>,
});

function fmtDate(d: string | null | undefined): string {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return d;
  }
}
